import React, { useState } from 'react';
import {
  FiCheck, FiX, FiInfo, FiAlertTriangle, FiRefreshCw, FiTrash2,
  FiChevronDown, FiChevronUp
} from 'react-icons/fi';

import PlantChat, { Message } from '../components/PlantChat';
import { PlantInfo } from '../types/api';
import { API_BASE_URL } from '../config/api';
import { formatPlantDisplayName, getInvasiveStatus } from '../utils/dataConversion';

// Fix for TS2786: Cast icons to any to resolve ReactNode vs Element type mismatch
const IconCheck = FiCheck as any;
const IconX = FiX as any;
const IconInfo = FiInfo as any;
const IconAlertTriangle = FiAlertTriangle as any;
const IconRefreshCw = FiRefreshCw as any;
const IconTrash2 = FiTrash2 as any;
const IconChevronDown = FiChevronDown as any;
const IconChevronUp = FiChevronUp as any;

interface ChatPageProps {
  currentPlant: {
    id: string;
    species?: string;
    description?: string;
    plantData?: PlantInfo; 
  } | null;
  messages: Message[];
  onNewMessage: (message: Message) => void;
  onNavigateToCollection: () => void;
  onNavigateToUpload: () => void;
  userRole: string;
}

const ChatPage: React.FC<ChatPageProps> = ({ 
  currentPlant, 
  messages, 
  onNewMessage,
  onNavigateToCollection,
  onNavigateToUpload,
  userRole
}) => {
  const [activeSection, setActiveSection] = useState<'effects' | 'alternatives' | 'removal' | null>(null);
  const [feedbackChoice, setFeedbackChoice] = useState<'up' | 'down' | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'wrong-plant' | 'wrong-invasive' | 'wrong-native-region' | ''>('');
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const handleThumbUp = async () => {
    if (!currentPlant?.plantData) return;
    setFeedbackChoice('up');
    try {
      await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plantId: currentPlant.id,
          specieIdentified: currentPlant.plantData.scientificName || currentPlant.plantData.commonName,
          isInvasive: currentPlant.plantData.isInvasive,
          feedbackType: 'thumbs-up',
          context: { source: 'compact-results-view' }
        })
      });
    } catch (e) { console.error("Feedback error", e); }
  };

  const handleThumbDownClick = () => {
    if (!currentPlant?.plantData) return;
    setFeedbackChoice('down');
    setShowFeedbackModal(true);
    setFeedbackError(null);
    setFeedbackType('');
    setFeedbackText('');
  };

  const handleSubmitFeedback = async () => {
    if (!currentPlant?.plantData || !feedbackType) {
      setFeedbackError('Please select a reason.');
      return;
    }
    setIsSubmittingFeedback(true);
    try {
      await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plantId: currentPlant.id,
          feedbackType,
          comment: feedbackText || null,
          context: { source: 'compact-results-view' }
        })
      });
      setShowFeedbackModal(false);
    } catch (e) {
      setFeedbackError('Could not submit feedback. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const invasiveStatus = currentPlant?.plantData ? getInvasiveStatus(currentPlant.plantData) : null;
  const headerStatusClass = invasiveStatus === 'invasive' ? 'invasive' : 'non-invasive';
  const nativeRegion = currentPlant?.plantData?.nativeRegion;
  const showNativeRegion = nativeRegion && !['N/A', 'Unknown', ''].includes(nativeRegion);
  const showInvasiveButtons = invasiveStatus === 'invasive' || invasiveStatus === 'native-invasive';

  return (
    <section className="chat-page results-section">
      <div className="container">
        {currentPlant?.plantData ? (
          <>
            <div className={`results-header-compact ${headerStatusClass}`}>
              <div className="header-text-group">
                <h1 className="status-title-compact">
                  {formatPlantDisplayName(currentPlant.plantData.scientificName, currentPlant.plantData.commonName)}
                </h1>
                <p className="scientific-name-mini">{currentPlant.plantData.scientificName}</p>
                {showNativeRegion && (
                  <p className="native-region-mini">{nativeRegion}</p>
                )}
              </div>
              <div className="header-meta-group">
                <span className={`invasive-status-pill ${invasiveStatus === 'invasive' ? 'invasive' : 'native'}`}>
                  {invasiveStatus === 'invasive' ? 'INVASIVE' : 'NATIVE'}
                </span>
              </div>
            </div>

            <div className="results-content">
              <div className="analysis-summary-container">
                <div className="mini-score-visual">
                  <svg viewBox="0 0 36 36" className="circular-chart-small">
                    <path className="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    <path className="circle" stroke="#4caf50" strokeDasharray={`${currentPlant.plantData.confidenceScore}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  </svg>
                  <div className="score-text-small">
                    <span className="score-number-small">{currentPlant.plantData.confidenceScore}%</span>
                    <span className="score-label-small">MATCH</span>
                  </div>
                </div>

                <div className="reasoning-content-right">
                  <div className="reasoning-header-row">
                    <span className="reasoning-label-mini"><IconInfo /> ANALYSIS SUMMARY</span>
                    <div className="mini-feedback-tools">
                      <span className="feedback-label">Accurate?</span>
                      <button 
                        className={`thumb-btn up ${feedbackChoice === 'up' ? 'active' : ''}`} 
                        onClick={handleThumbUp}
                      >
                        <IconCheck className="feedback-icon" style={{color: 'white', width: '20px', height: '20px'}} />
                      </button>
                      <button 
                        className={`thumb-btn down ${feedbackChoice === 'down' ? 'active' : ''}`} 
                        onClick={handleThumbDownClick}
                      >
                        <IconX className="feedback-icon" style={{color: 'white', width: '20px', height: '20px'}} />
                      </button>
                    </div>
                  </div>
                  <p>{currentPlant.plantData.confidenceReasoning}</p>
                </div>
              </div>

              {showInvasiveButtons && (
                <div className="quick-actions-section">
                  <div className="action-buttons-row">
                    <button className={`action-tile ${activeSection === 'effects' ? 'active' : ''}`} onClick={() => setActiveSection(activeSection === 'effects' ? null : 'effects')}>
                      <IconAlertTriangle className="tile-icon" /><span>Invasive Effects</span>{activeSection === 'effects' ? <IconChevronUp className="toggle-icon"/> : <IconChevronDown className="toggle-icon"/>}
                    </button>
                    {/* Additional buttons added to match previous requested functionality */}
                    <button className={`action-tile ${activeSection === 'alternatives' ? 'active' : ''}`} onClick={() => setActiveSection(activeSection === 'alternatives' ? null : 'alternatives')}>
                      <IconRefreshCw className="tile-icon" /><span>Native Alternatives</span>{activeSection === 'alternatives' ? <IconChevronUp className="toggle-icon"/> : <IconChevronDown className="toggle-icon"/>}
                    </button>
                    <button className={`action-tile ${activeSection === 'removal' ? 'active' : ''}`} onClick={() => setActiveSection(activeSection === 'removal' ? null : 'removal')}>
                      <IconTrash2 className="tile-icon" /><span>Removal Steps</span>{activeSection === 'removal' ? <IconChevronUp className="toggle-icon"/> : <IconChevronDown className="toggle-icon"/>}
                    </button>
                  </div>
                </div>
              )}

              {/* Content Panels for Invasive Information */}
              {activeSection === 'effects' && (
                <div className="action-content-panel">
                  <h3>Invasive Effects</h3>
                  <p>{currentPlant.plantData.impact}</p>
                </div>
              )}
              {activeSection === 'alternatives' && (
                <div className="action-content-panel">
                  <h3>Native Alternatives</h3>
                  <div className="alternatives-list">
                    {currentPlant.plantData.nativeAlternatives.length > 0 ? (
                      currentPlant.plantData.nativeAlternatives.map((alternative, index) => (
                        <div key={index} className="alternative-item">
                          <strong>{alternative.commonName}</strong>
                          <span className="scientific-name-small">{alternative.scientificName}</span>
                          <p>{alternative.description}</p>
                          <div className="benefits">
                            {alternative.benefits.map((benefit, benefitIndex) => (
                              <span key={benefitIndex} className="benefit-tag">{benefit}</span>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p>No native alternatives available for this plant.</p>
                    )}
                  </div>
                </div>
              )}
              {activeSection === 'removal' && (
                <div className="action-content-panel">
                  <h3>Removal Steps</h3>
                  {currentPlant.plantData.controlMethods.length > 0 ? (
                    <ul>
                      {currentPlant.plantData.controlMethods.map((method, index) => (
                        <li key={index}>{method}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>No removal steps available for this plant.</p>
                  )}
                </div>
              )}

              <div className="chat-container-focus">
                <PlantChat plantData={currentPlant.plantData} messages={messages} onNewMessage={onNewMessage} userRole={userRole} />
              </div>
            </div>

            <div className="results-actions">
              <button className="button-secondary" onClick={onNavigateToUpload}>SCAN ANOTHER</button>
              <button className="button-primary" onClick={onNavigateToCollection}>VIEW COLLECTION</button>
            </div>

            {showFeedbackModal && (
              <div className="feedback-modal-backdrop">
                <div className="feedback-modal">
                  <h3 className="feedback-title">What was the issue?</h3>
                  <div className="feedback-options">
                    <button type="button" className={`feedback-option ${feedbackType === 'wrong-plant' ? 'selected' : ''}`} onClick={() => setFeedbackType('wrong-plant')}>Wrong plant species</button>
                    <button type="button" className={`feedback-option ${feedbackType === 'wrong-invasive' ? 'selected' : ''}`} onClick={() => setFeedbackType('wrong-invasive')}>Wrong about invasiveness</button>
                    <button type="button" className={`feedback-option ${feedbackType === 'wrong-native-region' ? 'selected' : ''}`} onClick={() => setFeedbackType('wrong-native-region')}>Wrong about native region</button>
                  </div>
                  <textarea className="feedback-textarea" placeholder="Add any details..." value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} />
                  {feedbackError && <div className="feedback-error">{feedbackError}</div>}
                  <div className="feedback-actions">
                    <button className="feedback-cancel" onClick={() => setShowFeedbackModal(false)}>Cancel</button>
                    <button className="feedback-submit" onClick={handleSubmitFeedback} disabled={isSubmittingFeedback}>{isSubmittingFeedback ? 'Submitting...' : 'Submit Feedback'}</button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : <div className="empty-state">No plant data found.</div>}
      </div>
    </section>
  );
};

export default ChatPage;